import { Injectable } from '@angular/core';
import { Tenant } from '@ao/app/shared/interfaces/Tenant';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ClientContextService {
  private readonly storageKey = 'selectedClient';
  private clientSubject: BehaviorSubject<Tenant | null>;

  /** An observable that emits the current client whenever it changes. */
  public clientChanges$: Observable<Tenant | null>;

  constructor() {
    const storedClient = this.getClientFromStorage();
    this.clientSubject = new BehaviorSubject<Tenant | null>(storedClient);
    this.clientChanges$ = this.clientSubject.asObservable();
  }

  /**
   * Sets the current client for the application and persists it to session storage.
   * @param client The client to set as the current context.
   */
  public setCurrentClient(client: Tenant): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(client));
    this.clientSubject.next(client);
  }

  /**
   * Gets the current client from the behavior subject.
   * @returns The current client, or null if none is set.
   */
  public getCurrentClient(): Tenant | null {
    return this.clientSubject.getValue();
  }

  /**
   * Clears the current client from the context and session storage.
   * This will trigger the client selection dialog to appear on next check.
   */
  public clearCurrentClient(): void {
    sessionStorage.removeItem(this.storageKey);
    this.clientSubject.next(null);
  }

  /**
   * Retrieves the client from session storage on initialization.
   * @returns The stored client, or null if none is found.
   */
  private getClientFromStorage(): Tenant | null {
    const storedItem = sessionStorage.getItem(this.storageKey);
    if (storedItem) {
      try {
        return JSON.parse(storedItem) as Tenant;
      } catch (e) {
        sessionStorage.removeItem(this.storageKey);
        return null;
      }
    }
    return null;
  }
}
